import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

interface PageGuardProps {
  pageId: string;
  children: React.ReactNode;
}

const PageGuard = ({ pageId, children }: PageGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organization, isLoading: orgLoading } = useOrganization();

  useEffect(() => {
    // Wait for organization context to load
    if (orgLoading || !organization?.id) return;

    const checkPageAccess = async () => {
      try {
        // Check for preview mode
        const params = new URLSearchParams(location.search);
        const isPreviewMode = params.get("preview") === "draft";

        // Query page for this organization specifically
        const { data: pageData } = await supabase
          .from("pages")
          .select("status, redirect_to")
          .eq("page_id", pageId)
          .eq("organization_id", organization.id)
          .maybeSingle();

        // If page doesn't exist for this organization, redirect to homepage
        if (!pageData) {
          navigate("/", { replace: true });
          return;
        }

        // Check if page is live
        if (pageData.status === "live") {
          // Handle redirect if configured (but not in preview mode)
          if (pageData.redirect_to && !isPreviewMode) {
            navigate(pageData.redirect_to, { replace: true });
          }
          return;
        }

        // Page is draft - check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          const { data: roles } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", user.id)
            .in("role", ["admin", "super_admin"])
            .maybeSingle();

          if (roles) {
            // Admin can access draft pages (always, but especially in preview mode)
            return;
          }
        }

        // Not admin, page is draft - redirect (ignore preview param for non-admins)
        navigate(pageData.redirect_to || "/", { replace: true });
      } catch (error) {
        console.warn("PageGuard check failed:", error);
        // Fail to homepage for safety
        navigate("/", { replace: true });
      }
    };

    checkPageAccess();
  }, [pageId, navigate, location.search, organization?.id, orgLoading]);

  // ALWAYS render children immediately - no "Loading..." screen
  // Access checks happen in background and redirect if needed
  return <>{children}</>;
};

export default PageGuard;
